import { Operation, ServerParseError } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { LocationPaths } from 'location.types'
import { clearAuthToken } from './authTokenLink'
import { MFA_TOKEN_HEADER, clearMfaToken, getMfaToken } from './mfaTokenLink'

const redirectToLoginErrorCodes = [401, 403]

// The backend rejects requests whose MFA token is missing, expired or revoked
// with this GraphQL error (in a 200 response), not with a 401.
export const INVALID_MFA_TOKEN_ERROR = 'invalid_mfa_token'

const PUBLIC_PATHS: string[] = [LocationPaths.Login, LocationPaths.ResetPasswordRequest]

const onPublicPath = () => PUBLIC_PATHS.includes(window.location.pathname)

const navigateToLogin = () => {
  if (onPublicPath()) return

  window.location.href = LocationPaths.Login
}

// Credentials live in localStorage, shared by every tab: a rejection that
// arrives after another tab stored a new MFA token must not clear it.
const sentCurrentMfaToken = (operation: Operation) =>
  (operation.getContext().headers?.[MFA_TOKEN_HEADER] ?? null) === getMfaToken()

export const isInvalidMfaTokenError = (errors?: ReadonlyArray<{ message: string }>) =>
  !!errors?.some(({ message }) => message === INVALID_MFA_TOKEN_ERROR)

const signOut = () => {
  clearAuthToken()
  clearMfaToken()
  navigateToLogin()
}

export const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (
    networkError &&
    redirectToLoginErrorCodes.includes((networkError as ServerParseError).statusCode)
  ) {
    signOut()
    return
  }

  if (isInvalidMfaTokenError(graphQLErrors) && sentCurrentMfaToken(operation)) {
    // On the login screen the admin may be mid-challenge, so keep their auth
    // token there; AuthProvider moves the sign-in on to the SMS challenge.
    if (onPublicPath()) {
      clearMfaToken()
      return
    }

    signOut()
  }
})
